<template>
  <InfuseModal
    :title="isUpdate ? t('iam.tenants.header.update_tenant') : t('iam.tenants.header.create_tenant')"
    @close="emit('close')"
    :disable-close="loading"
  >
    <form :id="formId" @submit.prevent="onSubmit" class="flex flex-col gap-2">
      <InfuseField :label="t('iam.tenants.name')">
        <InfuseInput
          :required="true"
          v-model="nameMutation"
          ref="nameInputRef"
          :placeholder="t('iam.tenants.name_placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('iam.tenants.description')">
        <InfuseInput
          :required="false"
          v-model="descriptionMutation"
          ref="descriptionInputRef"
          :placeholder="t('iam.tenants.description_placeholder')"
        />
      </InfuseField>
    </form>
    <template #actions>
      <InfuseButton color="secondary" type="button" @click="emit('close')" :disabled="loading">
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton type="submit" :form="formId" :disabled="loading" :loading="loading">
        {{ t('action.submit') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { CreateTenant, Tenant, UpdateTenant } from '@infuse/api/iam/tenant';
import { InfuseButton, InfuseField, InfuseInput, InfuseModal } from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const nameMutation = ref('');
const descriptionMutation = ref<string | undefined>('');

const isUpdate = computed(() => !!props.tenant);
const formId = computed(() => (isUpdate.value ? 'update-tenant' : 'add-tenant'));

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    tenant?: Tenant;
  }>(),
  {
    loading: false
  }
);

interface Emits {
  (event: 'close'): void;
  (event: 'mutate', vars: { id: string | undefined; tenant: CreateTenant | UpdateTenant }): void;
}

const emit = defineEmits<Emits>();

watch(
  () => props.tenant,
  (tenant) => {
    if (!tenant) {
      return;
    }
    nameMutation.value = tenant.name;
    descriptionMutation.value = tenant.description;
  },
  { immediate: true }
);

const onSubmit = () => {
  emit('mutate', {
    id: props.tenant?.id,
    tenant: {
      name: nameMutation.value,
      description: descriptionMutation.value
    }
  });
};
</script>
